// ===== Identidade de aparelho DECLARADA PELO CLIENTE: a fronteira, uma vez só =====
//
// Este modulo e a UNICA implementacao das regras que decidem se um identificador de
// aparelho vindo do cliente e aceitavel. Ele vive em `packages/shared` — e nao em
// `apps/api` — por um motivo especifico: a borda da API (`apps/api/src/auth/
// device-bucket-key.ts`) e o contrato PUBLICADO (`DeviceInfoSchema`, aqui neste
// pacote) precisam da MESMA regra, e a primeira versao desta entrega duplicou o
// predicado nos dois lados, amarrando-os por um teste de concordancia. Um teste de
// concordancia so cobre os valores que ele enumera; duas implementacoes divergem
// exatamente nos valores que ninguem lembrou de enumerar. Com uma implementacao so,
// nao existe divergencia possivel — nem valor que precise ser lembrado.
//
// ⚠️ ES5 ESTRITO, E ISSO NAO E ESTILO. Este pacote entra no bundle do app de TV, cujo
// piso e Chromium 38 (webOS 2017). `String.prototype.startsWith` e `includes` sao
// ES2015 (Chr41+) e lancam TypeError la; escape de propriedade Unicode em regex
// (`\p{...}` + flag `u`) e SyntaxError FATAL no Chr53, que derruba o app no PARSE, nao
// na chamada. Por isso tudo aqui e `indexOf`, `charCodeAt` e laco.
/** Marcador do espaco de chave reservado ao SERVIDOR. Nunca aceite isto do cliente. */
export const SERVER_DEVICE_KEY_PREFIX = 'dev:';
/** Teto do identificador declarado. Espelha `devices.primary_id varchar(64)`. */
export const DEVICE_PRIMARY_ID_MAX = 64;
/** Recusa por invadir o espaco do servidor. E o codigo que o cliente ve no 400. */
export const DEVICE_KEY_REJECTED_RESERVED = 'reserved_device_key';
/** Recusa por forma que nenhum aparelho real emite (vazio/branco/invisivel/controle). */
export const DEVICE_KEY_REJECTED_MALFORMED = 'malformed_device_key';
/**
 * O valor invade o espaco de chave do SERVIDOR?
 *
 * Duas familias:
 *
 *  - o PREFIXO `dev:`, em qualquer caixa e com espaco em volta;
 *  - um UUID cru, que e a forma de um `devices.id`. Mesmo sem prefixo, ele e
 *    identificador emitido pelo servidor e nao tem por que chegar como "o DUID do meu
 *    aparelho" — recusar e mais barato que raciocinar sobre onde ele colidiria depois.
 *
 * ⚠️ O UUID e reconhecido nas QUATRO grafias que o Postgres aceita e normaliza para o
 * mesmo valor: canonica hifenizada, sem hifen, entre chaves e com prefixo `urn:uuid:`.
 * A versao anterior casava so a canonica. Hoje nenhum consumidor faz `primary_id::uuid`
 * — mas a rede existe justamente para quem escrever esse cast depois, e uma rede que
 * cobre 1 de 4 grafias entrega ao proximo leitor uma garantia que ela nao tem.
 */
export function isReservedDeviceKey(declared) {
    const v = trimAscii(declared).toLowerCase();
    if (v.indexOf(SERVER_DEVICE_KEY_PREFIX) === 0)
        return true;
    return isUuidAnyForm(v);
}
/**
 * O valor tem forma que NENHUM aparelho real produz?
 *
 * `true` = RECUSAR (400). Tres classes, todas medidas no codigo de hoje:
 *
 *  - **vazio / so espaco**: `z.string().max(64)` aceitava `''`. No fluxo Xtream
 *    (`routes/auth.ts`) isso cria UMA linha de `devices` por provider que TODO cliente
 *    sem DUID passa a compartilhar — cota, sessao e ficha de gente diferente na mesma
 *    linha. (No fluxo B2C o `''` caia por acidente, no falsy de `upsertDevice`.)
 *
 *  - **caractere invisivel ou de controle**: `primary_id` e renderizado na busca do
 *    painel do parceiro e sai no CSV do admin. A versao anterior barrava so C0+DEL, o
 *    que deixava passar exatamente os caracteres que produzem o dano nomeado: o
 *    zero-width space, que faz `<ZWSP>dev:<uuid>` renderizar IDENTICO a uma chave
 *    emitida pelo servidor para quem esta no suporte; o RIGHT-TO-LEFT OVERRIDE, que
 *    inverte a leitura da linha; e LINE/PARAGRAPH SEPARATOR e os controles C1, que
 *    quebram linha em parser de log e de CSV. `String.prototype.trim` remove NBSP e
 *    BOM mas NAO remove ZWSP, WORD JOINER, SOFT HYPHEN nem MONGOLIAN VOWEL SEPARATOR
 *    — por isso a checagem e explicita, e nao delegada ao trim.
 *
 *  - **surrogate desemparelhado**: UTF-16 invalido. O driver do Postgres grava
 *    U+FFFD no lugar, entao o valor ARMAZENADO deixa de ser o que o aparelho mandou —
 *    e a promessa central desta fronteira e que `primary_id` fica identico ao emitido.
 *    Recusar preserva a promessa; aceitar a quebraria em silencio.
 *
 * ⚠️ E uma DENYLIST de invisiveis, deliberadamente, e nao uma allowlist de charset. As
 * tres formas reais de hoje (`tizen-duid:` / `webos-lgudid:` / `web-uuid:`) sao
 * alfanumericas com hifen e passariam numa allowlist — mas um DUID de fabricante que
 * ainda nao vimos pode trazer charset que nao conhecemos, e derrubar esse aparelho no
 * boot e mais caro que o buraco que a allowlist fecharia. Nenhum DUID real contem
 * ZWSP ou RLO; a denylist e segura para campo e cobre o dano nomeado acima.
 *
 * NAO normaliza (nao faz trim, nao filtra): sanitizar em silencio faria o servidor
 * gravar uma chave DIFERENTE da que o aparelho manda, e o proximo login nao acharia a
 * linha. Recusar mantem `primary_id` identico ao que o aparelho emite.
 */
export function isMalformedDeviceKey(declared) {
    if (trimAscii(declared).length === 0)
        return true;
    if (hasInvisibleOrControlChar(declared))
        return true;
    if (hasLoneSurrogate(declared))
        return true;
    return false;
}
/**
 * Esta mensagem de issue Zod e uma recusa de IDENTIDADE DE APARELHO?
 *
 * Usada pelo error handler global da API para nomear o evento no log. Ver o bloco de
 * observabilidade em `apps/api/src/lib/error-handler.ts`.
 */
export function isDeviceKeyRejection(message) {
    return message === DEVICE_KEY_REJECTED_RESERVED || message === DEVICE_KEY_REJECTED_MALFORMED;
}
// ===== Helpers privados (ES5) =====
/** `trim` proprio: so espaco/tab/quebra ASCII. NAO tenta cobrir invisivel Unicode —
 *  quem cobre e `hasInvisibleOrControlChar`, que RECUSA em vez de aparar. */
function trimAscii(v) {
    let inicio = 0;
    let fim = v.length;
    while (inicio < fim && isAsciiSpace(v.charCodeAt(inicio)))
        inicio++;
    while (fim > inicio && isAsciiSpace(v.charCodeAt(fim - 1)))
        fim--;
    return v.slice(inicio, fim);
}
function isAsciiSpace(c) {
    return c === 0x20 || c === 0x09 || c === 0x0a || c === 0x0d || c === 0x0b || c === 0x0c;
}
/**
 * Invisivel (Cf), controle (Cc: C0+C1+DEL) ou separador de linha/paragrafo (Zl/Zp).
 *
 * Faixas explicitas por CODIGO. Nao usa escape de propriedade Unicode (`\p{Cf}`) porque
 * ele exige a flag `u`, que e SyntaxError FATAL no Chr53 — derruba o app de TV no
 * PARSE, nao na chamada. Nao usa classe de regex com escape hexadecimal porque o ESLint
 * barra pelo `no-control-regex`, e nao usa o glifo literal porque o
 * `no-irregular-whitespace` marca o caractere-FONTE. O laco nao briga com as tres.
 *
 * ⚠️ INCLUI O ESPACO UNICODE (NBSP, EN QUAD, IDEOGRAPHIC SPACE...). Nao e zelo: o
 * `trimAscii` acima apara so branco ASCII, de proposito, entao quem nao estiver aqui
 * ATRAVESSA o guard de espaco reservado. Foi assim que `<NBSP>dev:<uuid>` passou na
 * primeira rodada desta correcao — a matriz do teste pegou. Espaco ASCII INTERNO segue
 * permitido (`tizen-duid:AB CD` e visivel e sem ambiguidade); o que se recusa aqui e o
 * branco que RENDERIZA como outra coisa.
 */
function hasInvisibleOrControlChar(v) {
    for (let i = 0; i < v.length; i++) {
        let c = v.charCodeAt(i);
        // Decodifica o par surrogate pra comparar por CODE POINT: as faixas mais perigosas
        // desta lista (TAG, variation selectors suplementares) sao astrais, e comparar por
        // unidade UTF-16 nunca as alcancaria. `codePointAt` e ES2015 — nao da, Chr38.
        if (c >= 0xd800 && c <= 0xdbff && i + 1 < v.length) {
            const baixo = v.charCodeAt(i + 1);
            if (baixo >= 0xdc00 && baixo <= 0xdfff) {
                c = (c - 0xd800) * 0x400 + (baixo - 0xdc00) + 0x10000;
                i++;
            }
        }
        if (isForbiddenCodePoint(c))
            return true;
    }
    return false;
}
/**
 * Code point que NAO deve aparecer num identificador de aparelho, por CATEGORIA.
 *
 * ⚠️ A versao anterior era uma lista de casos vistos, e deixava passar 396+ code points
 * — variation selectors (`FE00..FE0F`), TAG (`E0000..E007F`, que carrega um alfabeto
 * ASCII invisivel inteiro), HANGUL FILLER (`3164`, que renderiza como espaco em branco)
 * e os selectors suplementares. Lista de exemplos nao fecha classe; ela so registra o
 * que alguem lembrou. Aqui a regra e por CATEGORIA nomeada, e as faixas sao a
 * transcricao dela:
 *
 *   - `Cc` — controles C0, DEL e C1;
 *   - `Zs` — espaco Unicode que o `trimAscii` nao apara;
 *   - `Zl` / `Zp` — LINE e PARAGRAPH SEPARATOR;
 *   - `Default_Ignorable_Code_Point` — a propriedade Unicode que significa, literalmente,
 *     "nao deve ser renderizado". E ela que cobre ZWSP, WORD JOINER, os bidi overrides,
 *     os variation selectors e o bloco TAG de uma vez so;
 *   - nao-caracteres e marcas de anotacao interlinear.
 *
 * Escrita como faixas, e nao como `\p{Default_Ignorable_Code_Point}`, porque o escape de
 * propriedade Unicode exige a flag `u` — SyntaxError FATAL no Chr53, que derruba o app
 * de TV no PARSE. A faixa e a mesma regra; so nao pode ser expressa pelo atalho.
 *
 * Continua DENYLIST: as tres formas reais sao alfanumericas com hifen e passariam numa
 * allowlist de charset, mas um DUID de fabricante que ainda nao vimos pode trazer
 * charset desconhecido, e derrubar esse aparelho no boot custa mais que o buraco. Nenhum
 * DUID real contem TAG ou variation selector.
 */
function isForbiddenCodePoint(c) {
    // Cc — C0, DEL e C1 (inclui NEL 0x85).
    if (c <= 0x1f || (c >= 0x7f && c <= 0x9f))
        return true;
    // Zs — espaco Unicode. O `trimAscii` so apara branco ASCII, entao quem nao estiver
    // aqui ATRAVESSA o guard de espaco reservado (foi assim que o NBSP passou).
    if (c === 0x00a0 || c === 0x1680 || c === 0x202f || c === 0x205f || c === 0x3000)
        return true;
    if (c >= 0x2000 && c <= 0x200a)
        return true;
    // Zl / Zp — quebram linha em parser de log e de CSV.
    if (c === 0x2028 || c === 0x2029)
        return true;
    // Default_Ignorable_Code_Point.
    if (c === 0x00ad || c === 0x034f || c === 0x061c)
        return true;
    if (c >= 0x115f && c <= 0x1160)
        return true; // HANGUL CHOSEONG/JUNGSEONG FILLER
    if (c >= 0x17b4 && c <= 0x17b5)
        return true; // KHMER VOWEL INHERENT AQ/AA
    if (c >= 0x180b && c <= 0x180f)
        return true; // MONGOLIAN FVS + VOWEL SEPARATOR
    if (c >= 0x200b && c <= 0x200f)
        return true; // ZWSP, ZWNJ, ZWJ, LRM, RLM
    if (c >= 0x202a && c <= 0x202e)
        return true; // LRE..RLO (bidi override)
    if (c >= 0x2060 && c <= 0x206f)
        return true; // WORD JOINER, isolates, deprecated bidi
    if (c === 0x3164)
        return true; // HANGUL FILLER (renderiza como branco)
    if (c >= 0xfe00 && c <= 0xfe0f)
        return true; // VARIATION SELECTOR-1..16
    if (c === 0xfeff)
        return true; // BOM / ZWNBSP
    if (c === 0xffa0)
        return true; // HALFWIDTH HANGUL FILLER
    if (c >= 0xfff0 && c <= 0xfff8)
        return true; // nao-atribuidos reservados a formatacao
    if (c >= 0x1bca0 && c <= 0x1bca3)
        return true; // SHORTHAND FORMAT CONTROLS
    if (c >= 0x1d173 && c <= 0x1d17a)
        return true; // MUSICAL SYMBOL BEAM/SLUR (formatacao)
    if (c >= 0xe0000 && c <= 0xe0fff)
        return true; // TAG + VARIATION SELECTOR SUPPLEMENT
    // Marcas de anotacao interlinear e nao-caracteres.
    if (c >= 0xfff9 && c <= 0xfffb)
        return true;
    if (c === 0xfffe || c === 0xffff)
        return true;
    return false;
}
/** Surrogate alto sem par baixo (ou vice-versa) = UTF-16 invalido. */
function hasLoneSurrogate(v) {
    for (let i = 0; i < v.length; i++) {
        const c = v.charCodeAt(i);
        if (c >= 0xd800 && c <= 0xdbff) {
            const proximo = i + 1 < v.length ? v.charCodeAt(i + 1) : -1;
            if (proximo < 0xdc00 || proximo > 0xdfff)
                return true;
            i++;
        }
        else if (c >= 0xdc00 && c <= 0xdfff) {
            return true;
        }
    }
    return false;
}
/** UUID nas quatro grafias que o Postgres normaliza para o mesmo valor. Recebe o valor
 *  ja em minusculas. */
function isUuidAnyForm(v) {
    let s = v;
    if (s.indexOf('urn:uuid:') === 0)
        s = s.slice(9);
    if (s.length > 1 && s.charAt(0) === '{' && s.charAt(s.length - 1) === '}') {
        s = s.slice(1, s.length - 1);
    }
    let hex = '';
    for (let i = 0; i < s.length; i++) {
        const ch = s.charAt(i);
        if (ch === '-')
            continue;
        hex += ch;
    }
    if (hex.length !== 32)
        return false;
    for (let i = 0; i < 32; i++) {
        const c = hex.charCodeAt(i);
        const ehDigito = c >= 0x30 && c <= 0x39;
        const ehHexLetra = c >= 0x61 && c <= 0x66;
        if (!ehDigito && !ehHexLetra)
            return false;
    }
    return true;
}
//# sourceMappingURL=device-key.js.map